import { useTranslation } from "react-i18next";
import type { AuthSession } from "../../../auth/keycloak.js";
import { FacilityCountsMeta } from "../FacilityCountsMeta.js";
import { FacilityManualsView } from "../FacilityManualsView.js";
import { ManualIcon } from "@/components/icons";

/** Minimal shape the pane renders — decoupled from the wire/tree types (the caller computes the counts + canManage). */
export interface SelectedFacility {
  id: number;
  name: string;
  childCount: number;
  categoryCount: number;
  manualCount: number;
  /** The caller can manage this facility (manage/access_manage or bypass). Computed by the caller; retained for the
   *  right pane's future manage affordances (the "Manage facility" action now lives on each card's kebab menu). */
  canManage: boolean;
  /** The caller can manage this facility's MANUALS (`manuals_manage`+ or bypass) — gates the manual kebab actions
   *  (best-effort; the server is authoritative for the state-dependent `confirm` nuance). WIDER than `canManage`. */
  canManageManuals: boolean;
  /** The caller has the facility `confirm` tier (confirm/manage/access_manage or bypass) — reveals the publish/unpublish/
   *  clear-flag review transitions (ADR-018). A subset of `canManageManuals`. */
  canConfirmManuals: boolean;
}

/**
 * The right pane of the master-detail layout — the selected facility's MANUALS (MV2). The header shows the name +
 * icon counts; below it {@link FacilityManualsView} renders the manuals card grid (filter chips, search, per-manual
 * favourite). Facility management lives on each tree card's kebab (⋮) menu, so this pane carries no manage button —
 * the manuals keep full width. The view stays mounted across selection changes (its toolbar resets on facility change,
 * but the client-only favourites persist — mirroring how F1 will make them stick).
 */
export function FacilityDetailPane({
  facility,
  session,
}: {
  facility: SelectedFacility | null;
  session: AuthSession | null;
}) {
  const { t } = useTranslation();
  return (
    <section aria-label={t("facilitySelection.facilityManuals")} className="flex min-h-0 min-w-0 flex-1 flex-col">
      {facility ? (
        <>
          <header className="border-border bg-background flex items-start justify-between gap-3 border-b px-6 py-4">
            <div className="min-w-0">
              <h2 className="truncate text-lg font-semibold">{facility.name}</h2>
              {/* Same icon+count meta as the tree card (subfacilities · categories · manuals); each omitted when zero. */}
              <FacilityCountsMeta
                className="mt-1 text-sm"
                subfacilities={facility.childCount}
                categories={facility.categoryCount}
                manuals={facility.manualCount}
              />
            </div>
          </header>
          <FacilityManualsView
            session={session}
            facilityId={facility.id}
            facilityName={facility.name}
            canManage={facility.canManageManuals}
            canConfirm={facility.canConfirmManuals}
          />
        </>
      ) : (
        <EmptyState line={t("facilitySelection.selectAFacility")} />
      )}
    </section>
  );
}

function EmptyState({ line }: { line: string }) {
  return (
    <div className="text-muted-foreground flex flex-1 flex-col items-center justify-center gap-2 p-6 text-center">
      <ManualIcon aria-hidden="true" className="size-8 opacity-40" />
      <p className="text-sm">{line}</p>
    </div>
  );
}
